import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mic, Megaphone, Globe2, Mail, Star, Check } from "lucide-react";

const PACKAGES = [
  { name: "Episode Sponsor", icon: Mic, desc: "30–60 sec ad reads inside selected episodes. Lock down a show or run across the network." },
  { name: "Show Sponsor", icon: Megaphone, desc: "Presenting sponsor of a full season — pre-roll, mid-roll, branded segments and end card." },
  { name: "Website Sponsor", icon: Globe2, desc: "Persistent placement across SVNLive.com — home, episodes, and show pages." },
  { name: "Newsletter Sponsor", icon: Mail, desc: "Featured slot in The Signal newsletter to engaged opt-in subscribers." },
  { name: "Featured Partner", icon: Star, desc: "Top-tier integration across all properties — content, events, and digital." },
];

export default function Sponsors() {
  const [sponsors, setSponsors] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", company: "", package: "", budget: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/sponsor-inquiry", form);
      toast.success("Inquiry sent. We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", company: "", package: "", budget: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="sponsors-page">
      <div className="max-w-3xl">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-4">Partner With SVN</p>
        <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter">
          Reach the <span className="text-svn-red">right</span> audience.
        </h1>
        <p className="mt-6 text-lg text-neutral-300 font-body">
          A growing, engaged community across sports, food, culture, business
          and lifestyle. Premium podcast inventory, broadcast-quality
          production, and creators who actually move product.
        </p>
      </div>

      {/* PACKAGES */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="sponsors-packages">
        {PACKAGES.map((p) => (
          <div key={p.name} className="bg-[#0F0F0F] border border-white/5 hover:border-gold/40 transition-all p-6" data-testid={`package-${p.name.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="w-12 h-12 bg-gold/10 border border-gold/30 flex items-center justify-center mb-5">
              <p.icon className="text-gold" size={20} />
            </div>
            <h3 className="font-display text-xl font-bold">{p.name}</h3>
            <p className="mt-3 text-sm text-neutral-400 font-body">{p.desc}</p>
            <ul className="mt-5 space-y-2 text-sm text-neutral-300">
              <li className="flex items-center gap-2"><Check className="text-gold" size={14} /> Custom branded segments</li>
              <li className="flex items-center gap-2"><Check className="text-gold" size={14} /> Performance reporting</li>
              <li className="flex items-center gap-2"><Check className="text-gold" size={14} /> Cross-network promotion</li>
            </ul>
          </div>
        ))}
      </div>

      {/* CURRENT SPONSORS */}
      <div className="mt-24" data-testid="current-sponsors">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-gold mb-6">Trusted Partners</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {sponsors.map((s) => (
            <a key={s.id} href={s.website_url || "#"} target="_blank" rel="noreferrer" className="bg-[#0F0F0F] border border-white/5 hover:border-gold/40 p-8 transition" data-testid={`sponsor-current-${s.name.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="font-display font-black text-2xl text-gold">{s.name}</p>
              <p className="mt-2 text-sm text-neutral-400 font-body">{s.description}</p>
            </a>
          ))}
        </div>
      </div>

      {/* INQUIRY FORM */}
      <div className="mt-24 grid lg:grid-cols-2 gap-16 items-start" data-testid="sponsor-inquiry-section">
        <div>
          <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight">
            Let's <span className="text-gold">talk numbers.</span>
          </h2>
          <p className="mt-6 text-neutral-300 font-body">
            Drop your details and a member of the partnerships team will follow
            up within one business day with a deck, audience demographics, and
            inventory availability.
          </p>
        </div>
        <form onSubmit={submit} className="space-y-4 bg-[#0F0F0F] border border-white/10 p-8" data-testid="sponsor-inquiry-form">
          <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="inquiry-name" required />
          <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="inquiry-email" required />
          <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testId="inquiry-company" />
          <Field label="Package interest" value={form.package} onChange={(v) => setForm({ ...form, package: v })} testId="inquiry-package" placeholder="e.g., Show Sponsor" />
          <Field label="Budget range" value={form.budget} onChange={(v) => setForm({ ...form, budget: v })} testId="inquiry-budget" placeholder="$5k - $25k" />
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">Message</label>
            <textarea
              required
              rows={4}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              data-testid="inquiry-message"
              className="mt-2 w-full bg-black border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-gold"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="inquiry-submit-btn"
            className="bg-svn-red text-white font-black uppercase tracking-widest text-sm px-8 py-4 hover:bg-red-700 transition disabled:opacity-60"
          >
            {submitting ? "Sending…" : "Send Inquiry"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required, placeholder }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.3em] text-neutral-400">{label}</label>
      <input
        type={type}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-2 w-full bg-black border border-white/15 px-4 py-3 text-white focus:outline-none focus:border-gold"
      />
    </div>
  );
}
